import React from "react";
import { T, useT } from "@transifex/react";
import {
  StickyButtonContainer,
  SelectionInfoRow,
  SelectionPill,
  SelectionCount,
  SelectionSubtext,
  RequestCallButton,
  ButtonIcon,
} from "./styles";

export interface StickyRequestCallButtonProps {
  onClick: () => void;
  selectedCount?: number;
  /** Custom button text from admin config. Defaults to "Request a Call" when empty. */
  buttonText?: string;
  /** URL to an icon image for the button. No icon shown when empty. */
  buttonIcon?: string;
  "data-testid"?: string;
}

export function StickyRequestCallButton({
  onClick,
  selectedCount = 0,
  buttonText,
  buttonIcon,
  "data-testid": testId,
}: StickyRequestCallButtonProps) {
  const t = useT();
  const hasSelection = selectedCount > 0;
  const displayText = buttonText?.trim() || t("Request a Call");

  return (
    <StickyButtonContainer data-testid={testId || "StickyRequestCallButton"}>
      {/* Selection info */}
      <SelectionInfoRow>
        <SelectionPill data-testid="StickyRequestCallButton__SelectionPill">
          {hasSelection ? (
            <>
              <SelectionCount>{selectedCount}</SelectionCount>
              <T _str="items selected for follow-up" />
            </>
          ) : (
            <T _str="No followups selected" />
          )}
        </SelectionPill>
        {hasSelection && (
          <SelectionSubtext data-testid="StickyRequestCallButton__Subtext">
            <T _str="We'll connect you with the right team" />
          </SelectionSubtext>
        )}
      </SelectionInfoRow>

      {/* Request a Call button */}
      <RequestCallButton
        onClick={hasSelection ? onClick : undefined}
        disabled={!hasSelection}
        data-testid="StickyRequestCallButton__Button"
        aria-label={
          hasSelection
            ? `${displayText} - ${selectedCount} items selected for follow-up`
            : `${displayText} - No items selected`
        }
        aria-disabled={!hasSelection}
      >
        {buttonIcon?.trim() && (
          <ButtonIcon
            src={buttonIcon.trim()}
            alt=""
            aria-hidden="true"
            data-testid="StickyRequestCallButton__ButtonIcon"
          />
        )}
        {displayText}
      </RequestCallButton>
    </StickyButtonContainer>
  );
}
